import React, {
  useEffect,
  useState,
  useCallback,
  useRef
} from "react";

import {
  TextField,
  MenuItem,
  Typography,
  Box
} from "@mui/material";

import {
  useForm,
  Controller
} from "react-hook-form";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  getEmployees
} from "../../services/employeeService";

import {
  getCategories
} from "../../services/categoryService";

import {
  getPriorities
} from "../../services/priorityService";

import {
  getProjects
} from "../../services/projectService";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"]
  ],
  imageResize: {
    modules: ["Resize", "DisplaySize"]
  }
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "image",
  "width"
];

function TicketForm({
  defaultValues,
  onSubmit,
  submitRef,
  onValidityChange
}) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    trigger,
    formState: { errors, isValid, touchedFields, isSubmitted }
  } = useForm({ mode: "onChange" });

  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [projects, setProjects] = useState([]);

  const quillRef = useRef(null);

  const loadMasterData = useCallback(async () => {
    try {
      const employeeData = await getEmployees();
      const categoryData = await getCategories();
      const priorityData = await getPriorities();
      const projectData = await getProjects();

      setEmployees(employeeData);
      setCategories(categoryData);
      setPriorities(priorityData);
      setProjects(projectData);
    }
    catch (error) {
      console.error("Master Data Error:", error);
    }
  }, []);

  useEffect(() => {
    loadMasterData();

    if (defaultValues) {
      reset(defaultValues);
    }

    trigger();
  }, [defaultValues, reset, loadMasterData, trigger]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();

    if (!editor) {
      return undefined;
    }

    const handlePaste = (event) => {
      const items = event.clipboardData?.items;

      if (!items) {
        return;
      }

      const imageItem = Array.from(items).find((item) => item.type.startsWith("image/"));

      if (!imageItem) {
        return;
      }

      event.preventDefault();

      const file = imageItem.getAsFile();

      if (!file) {
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const range = editor.getSelection(true);
        editor.insertEmbed(range ? range.index : editor.getLength(), "image", reader.result, "user");
      };

      reader.readAsDataURL(file);
    };

    const editorRoot = editor.root;
    editorRoot.addEventListener("paste", handlePaste);

    return () => {
      editorRoot.removeEventListener("paste", handlePaste);
    };
  }, []);

  const handleFormSubmit = useCallback((data) => {
    const payload = {
      title: data.title,
      description: data.description,
      category_id: Number(data.category_id),
      priority_id: Number(data.priority_id),
      // New tickets always start Open; edits preserve the current status
      status_id: defaultValues?.status_id || 5,
      assigned_to: data.assigned_to ? Number(data.assigned_to) : null,
      project_id: data.project_id ? Number(data.project_id) : null
    };

    onSubmit(payload);
  }, [onSubmit, defaultValues]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = () => {
        handleSubmit(handleFormSubmit)();
      };
    }
  }, [submitRef, handleSubmit, handleFormSubmit]);

  return (
    <form>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.2
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: "#475569",
            fontWeight: 600,
            mb: 0.2
          }}
        >
          Ticket Information
        </Typography>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 0.8 }}>
            Title <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Enter ticket title"
            error={Boolean(errors.title) && (touchedFields.title || isSubmitted)}
            helperText={errors.title && (touchedFields.title || isSubmitted) ? "Title is required" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("title", { required: true })}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 0.8 }}>
            Description <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
          </Typography>

          <Box
            sx={{
              "& .ticket-description-editor .ql-editor": {
                minHeight: 150
              }
            }}
          >
            <Controller
              name="description"
              control={control}
              defaultValue={defaultValues?.description || ""}
              rules={{ required: true }}
              render={({ field }) => (
                <ReactQuill
                  ref={quillRef}
                  className="ticket-description-editor"
                  value={field.value}
                  onChange={field.onChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter your Description (min 100 words)"
                  style={{
                    fontSize: 16,
                    background: "#fff"
                  }}
                />
              )}
            />
          </Box>

          {errors.description && (touchedFields.description || isSubmitted) && (
            <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.7, ml: 1.7 }}>
              Description is required
            </Typography>
          )}
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 0.8 }}>
            Category <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            defaultValue={defaultValues?.category_id || ""}
            error={Boolean(errors.category_id) && (touchedFields.category_id || isSubmitted)}
            helperText={errors.category_id && (touchedFields.category_id || isSubmitted) ? "Please select a category" : ""}
            SelectProps={{ displayEmpty: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("category_id", { required: true })}
          >
            <MenuItem value="" disabled>
              Select category
            </MenuItem>

            {categories.map((item) => (
              <MenuItem
                key={item.category_id}
                value={item.category_id}
              >
                {item.category_name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 0.8 }}>
            Priority <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            defaultValue={defaultValues?.priority_id || ""}
            error={Boolean(errors.priority_id) && (touchedFields.priority_id || isSubmitted)}
            helperText={errors.priority_id && (touchedFields.priority_id || isSubmitted) ? "Please select a priority" : ""}
            SelectProps={{ displayEmpty: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("priority_id", { required: true })}
          >
            <MenuItem value="" disabled>
              Select priority
            </MenuItem>

            {priorities.map((item) => (
              <MenuItem
                key={item.priority_id}
                value={item.priority_id}
              >
                {item.priority_name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 0.8 }}>
            Assign To <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            defaultValue={defaultValues?.assigned_to || ""}
            error={Boolean(errors.assigned_to) && (touchedFields.assigned_to || isSubmitted)}
            helperText={errors.assigned_to && (touchedFields.assigned_to || isSubmitted) ? "Please select an employee" : ""}
            SelectProps={{ displayEmpty: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("assigned_to", { required: true })}
          >
            <MenuItem value="" disabled>
              Select employee
            </MenuItem>

            {employees.map((employee) => (
              <MenuItem
                key={employee.employee_id}
                value={employee.employee_id}
              >
                {employee.employee_name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 0.8 }}>
            Project <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            defaultValue={defaultValues?.project_id || ""}
            error={Boolean(errors.project_id) && (touchedFields.project_id || isSubmitted)}
            helperText={errors.project_id && (touchedFields.project_id || isSubmitted) ? "Please select a project" : ""}
            SelectProps={{ displayEmpty: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("project_id", { required: true })}
          >
            <MenuItem value="" disabled>
              Select project
            </MenuItem>

            {projects.map((project) => (
              <MenuItem
                key={project.project_id}
                value={project.project_id}
              >
                {project.project_name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </form>
  );
}

export default TicketForm;
